import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ObjectId } from 'mongodb';
import { Flag, AlertTriangle, Lock } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import useProfilePage from '../../../hooks/useProfilePage';
import BreadcrumbNav, { BreadcrumbItemProps } from '../../common/BreadcrumbNav';
import ReportUserModal from '../reportUserModal';
import { ExtendedSafeDatabaseUser, Warning } from '../../../types/types';

/**
 * Extended type that includes the optional fields we're using
 */
interface ExtendedUserData extends ExtendedSafeDatabaseUser {
  _id: ObjectId;
  followers?: Map<string, Date>;
  following?: Map<string, Date>;
}

/**
 * ProfilePage component displays a user's profile information
 * including followers, following, and other profile elements.
 */
const ProfilePage: React.FC = () => {
  const {
    userData,
    loading,
    error,
    isFollowing,
    isOwnProfile,
    followerCount,
    followingCount,
    friendshipDuration,
    successMessage,
    handleFollow,
    handleUnfollow,
    clearMessages,
    currentUser,
  } = useProfilePage();

  // Cast userData to our extended type
  const userDataExt = userData as ExtendedUserData | null;

  // Local state for dialogs
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  if (loading) {
    return (
      <div className='flex justify-center p-8'>
        <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className='container flex items-center justify-center min-h-screen py-8'>
        <Card className='w-[600px]'>
          <CardContent className='pt-6'>
            <p className='text-center text-muted-foreground'>
              {error || 'User not found. Please check the username and try again.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Define breadcrumb items
  const breadcrumbItems: BreadcrumbItemProps[] = [
    { label: 'Home', href: '/home' },
    { label: 'Users', href: '/users' },
    { label: userData.username },
  ];

  // Check if profile is private and it's not the user's own profile
  if (userData?.visibility === 'private' && !isOwnProfile) {
    return (
      <div className='container py-8'>
        <BreadcrumbNav items={breadcrumbItems} />

        <Card className='w-full max-w-3xl mx-auto mt-8'>
          <CardContent className='flex flex-col items-center justify-center py-16'>
            <Lock size={64} className='text-gray-400 mb-4' />
            <h2 className='text-2xl font-semibold text-center mb-2'>This profile is private</h2>
            <p className='text-muted-foreground text-center'>
              The owner of this profile has set it to private. Only they can view the content.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Only show report button if not viewing own profile
  const showReportButton = !isOwnProfile;

  return (
    <div className='container py-8'>
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav items={breadcrumbItems} />

      {successMessage && (
        <Alert className='mb-4 bg-green-50 text-green-900 border-green-200' onClick={clearMessages}>
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant='destructive' className='mb-4' onClick={clearMessages}>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className='mb-6'>
        <CardHeader className='flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
          <div className='flex items-center gap-4'>
            {userData.profilePicture ? (
              <img
                src={userData.profilePicture}
                alt={`${userData.username}'s profile`}
                className='h-16 w-16 rounded-full object-cover'
              />
            ) : (
              <div className='rounded-full bg-blue-500 text-white h-16 w-16 flex items-center justify-center text-2xl'>
                {userData.username.charAt(0).toUpperCase()}
              </div>
            )}

            <div>
              <CardTitle className='text-2xl mb-1'>{userData.username}</CardTitle>
              <div className='text-sm text-muted-foreground'>
                Joined {new Date(userData.dateJoined).toLocaleDateString()}
              </div>
              {friendshipDuration && !isOwnProfile && (
                <div className='text-sm font-medium text-green-600 mt-1'>
                  Friends {friendshipDuration}
                </div>
              )}
            </div>
          </div>

          <div className='flex flex-col sm:flex-row gap-2'>
            {(() => {
              if (isOwnProfile) {
                return (
                  <Link to={`/user/${userData.username}/settings`}>
                    <Button variant='outline'>Edit Profile</Button>
                  </Link>
                );
              }

              return (
                <>
                  {isFollowing ? (
                    <Button onClick={handleUnfollow} variant='outline' className='mr-2'>
                      Unfollow
                    </Button>
                  ) : (
                    <Button onClick={handleFollow} className='mr-2'>
                      Follow
                    </Button>
                  )}

                  {showReportButton && (
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() => setIsReportModalOpen(true)}
                      title='Report user'
                      className='report-button'>
                      <Flag className='h-4 w-4 text-red-500' />
                    </Button>
                  )}
                </>
              );
            })()}
          </div>
        </CardHeader>

        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
            <div className='md:col-span-2'>
              <h3 className='text-lg font-medium mb-2'>About</h3>
              <p className='text-muted-foreground'>
                {userData.biography || 'No biography available.'}
              </p>

              {/* Show warnings if the user has any */}
              {userData.hasWarnings && userData.warnings && userData.warnings.length > 0 && (
                <div className='mt-6'>
                  <div className='flex items-center mb-2'>
                    <AlertTriangle className='h-5 w-5 text-amber-500 mr-2' />
                    <h3 className='text-lg font-medium'>Account Warnings</h3>
                  </div>
                  <div className='bg-amber-50 border border-amber-200 rounded-md p-4'>
                    <p className='text-sm text-amber-700 mb-3'>
                      Your account has received {userData.warnings.length} warning(s). Please review
                      our community guidelines.
                    </p>
                    <div className='space-y-3'>
                      {userData.warnings.map((warning: Warning, index: number) => (
                        <div
                          key={index}
                          className='border-b border-amber-200 pb-2 last:border-0 last:pb-0'>
                          <p className='text-sm font-medium text-amber-800'>{warning.reason}</p>
                          <p className='text-xs text-amber-600 mt-1'>
                            Issued on {new Date(warning.date).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <h3 className='text-lg font-medium mb-2'>Stats</h3>
              <div className='grid grid-cols-2 gap-2'>
                <div
                  className='p-3 rounded-md border text-center cursor-pointer'
                  onClick={() => setShowFollowers(true)}>
                  <div className='font-bold'>{followerCount}</div>
                  <div className='text-sm text-muted-foreground'>Followers</div>
                </div>

                <div
                  className='p-3 rounded-md border text-center cursor-pointer'
                  onClick={() => setShowFollowing(true)}>
                  <div className='font-bold'>{followingCount}</div>
                  <div className='text-sm text-muted-foreground'>Following</div>
                </div>

                {/* Rank and Points */}
                <div className='p-3 rounded-md border text-center col-span-2 mt-2 bg-blue-50'>
                  <div className='mb-2 text-blue-700 font-medium'>Contribution Stats</div>
                  <div className='flex justify-between px-4'>
                    <div>
                      <div className='inline-flex items-center justify-center'>
                        <span className='bg-blue-100 text-blue-800 font-bold px-2.5 py-2 rounded-full text-sm border border-blue-200'>
                          Level {Math.floor((userData.rank || 0) / 15) + 1}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className='font-bold text-blue-800'>{userData.rank || 0}</div>
                      <div className='text-sm text-muted-foreground'>Points</div>
                    </div>
                  </div>
                  <div className='text-xs text-muted-foreground mt-3 px-2'>
                    <p>
                      Earn points and level up by contributing: comments (+2), posts (+4), and
                      answers (+6)!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {showFollowers && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-md'>
            <CardHeader>
              <CardTitle>Followers ({followerCount})</CardTitle>
            </CardHeader>
            <CardContent className='max-h-[60vh] overflow-auto'>
              {userDataExt?.followers && userDataExt.followers.size > 0 ? (
                <div className='space-y-2'>
                  {Array.from(userDataExt.followers.entries()).map(([username, followDate]) => (
                    <Link
                      key={username}
                      to={`/user/${username}`}
                      onClick={() => setShowFollowers(false)}
                      className='block p-2 border rounded-md hover:bg-gray-100 cursor-pointer'>
                      <span className='font-medium text-blue-600'>{username}</span>
                      <div className='text-xs text-gray-500 mt-1'>
                        Following since {new Date(followDate).toLocaleDateString()}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className='text-center text-muted-foreground'>No followers to display</p>
              )}
            </CardContent>
            <div className='p-4 border-t flex justify-end'>
              <Button variant='outline' onClick={() => setShowFollowers(false)}>
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {showFollowing && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-md'>
            <CardHeader>
              <CardTitle>Following ({followingCount})</CardTitle>
            </CardHeader>
            <CardContent className='max-h-[60vh] overflow-auto'>
              {userDataExt?.following && userDataExt.following.size > 0 ? (
                <div className='space-y-2'>
                  {Array.from(userDataExt.following.entries()).map(([username, followDate]) => (
                    <Link
                      key={username}
                      to={`/user/${username}`}
                      onClick={() => setShowFollowing(false)}
                      className='block p-2 border rounded-md hover:bg-gray-100 cursor-pointer'>
                      <span className='font-medium text-blue-600'>{username}</span>
                      <div className='text-xs text-gray-500 mt-1'>
                        Following since {new Date(followDate).toLocaleDateString()}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className='text-center text-muted-foreground'>No users to display</p>
              )}
            </CardContent>
            <div className='p-4 border-t flex justify-end'>
              <Button variant='outline' onClick={() => setShowFollowing(false)}>
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {showReportButton && currentUser && userData && (
        <ReportUserModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          reportedUser={userData}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default ProfilePage;
